import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  ShoppingCart, Settings, Image, Users, School, Package, Menu, X, Home,
  Globe, Printer, User, BarChart3, ShoppingBag,
} from 'lucide-react';
import { ContactWidget } from '@/components/ContactWidget';
import { DesktopNav } from '@/components/layout/DesktopNav';
import { MobileNav } from '@/components/layout/MobileNav';
import { UserMenu } from '@/components/layout/UserMenu';
import { Footer } from '@/components/layout/Footer';

import { API_BASE } from '@/lib/apiBase';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { getItemCount } = useCart();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('schoolpix_theme');
      return cached ? JSON.parse(cached) : null;
    } catch (e) { return null; }
  });

  const cartCount = getItemCount();

  useEffect(() => {
    fetch(API_BASE + '/api/site-settings')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setSiteSettings(d); })
      .catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = useMemo(() => ([
    { path: '/dashboard', label: t('nav.dashboard'), icon: Home },
    { path: '/gallery', label: t('nav.gallery'), icon: Image },
    { path: '/orders', label: t('nav.orders'), icon: Package },
  ]), [t]);

  const adminLinks = useMemo(() => ([
    { path: '/admin/classes', label: t('nav.classes'), icon: School },
    { path: '/admin/students', label: t('nav.students'), icon: Users },
    { path: '/admin/photos', label: t('nav.photos'), icon: Image },
    { path: '/admin/print-formats', label: t('nav.printFormats'), icon: Printer },
    { path: '/admin/bundles', label: t('nav.bundles'), icon: Package },
    { path: '/admin/users', label: t('nav.users'), icon: User },
    { path: '/admin/site-settings', label: t('nav.siteSettings'), icon: Settings },
    { path: '/admin/analytics', label: t('nav.analytics'), icon: BarChart3 },
    { path: '/admin/abandoned-carts', label: t('nav.abandonedCarts'), icon: ShoppingBag },
  ]), [t]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="glass sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3 group">
              {siteSettings?.has_custom_logo && (
                <img
                  src={API_BASE + '/api/site-settings/logo'}
                  alt="Logo"
                  className="w-8 h-8 object-contain rounded-sm"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <span className="font-bold text-lg tracking-tight group-hover:text-primary transition-colors duration-200">
                {siteSettings?.site_name || 'SchoolPix'}
              </span>
            </Link>

            <DesktopNav
              user={user}
              navLinks={navLinks}
              adminLinks={adminLinks}
              isActive={isActive}
            />

            {/* Right side */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost" size="sm"
                onClick={toggleLanguage}
                className="hidden sm:flex items-center gap-1 px-2 text-muted-foreground hover:text-foreground"
                data-testid="language-toggle"
              >
                <Globe className="w-4 h-4" />
                <span className="text-xs font-bold uppercase">{language}</span>
              </Button>

              <Link
                to="/cart"
                className="relative p-2 hover:bg-secondary rounded-sm transition-colors duration-200"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-xs font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>

              <UserMenu user={user} onLogout={handleLogout} />

              <Button
                variant="ghost"
                className="md:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <MobileNav
            user={user}
            navLinks={navLinks}
            adminLinks={adminLinks}
            isActive={isActive}
            onClose={() => setMobileMenuOpen(false)}
          />
        )}
      </header>

      <main className="flex-1">{children}</main>

      <ContactWidget />
      <Footer />
    </div>
  );
};
